import React, { useId } from "react"

import {
  supportsTraceContext,
  supportsTranscriptFilter,
} from "@/metrics/runtime"
import {
  Button,
  GroupLabel,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
} from "@coval/ui"
import { ExternalLink, Info, Plus, X } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import { FIELD_CONTAINER_STYLES, LABEL_STYLES } from "../styles"
import { TraceContextToggle } from "../trace-context"
import {
  TranscriptFilterDetails,
  TranscriptFilterToggle,
} from "../transcript-filter"
import {
  isAutomaticExpectedBehaviorEnabled,
  isEnabledAutomaticExpectedBehaviorMetric,
} from "../utils/isExpectedBehaviorCompositeMetric"
import { CompositeCriteriaFields } from "./CompositeCriteriaFields"
import { KnowledgeBaseDisableControl } from "./KnowledgeBaseDisableControl"
import { LLMModelSelect } from "./LLMModelSelect"
import { RequiredMarker } from "./RequiredMarker"

const DOCS_URL = "https://docs.coval.ai/concepts/metrics/prompting"

/**
 * Predefined criteria source options.
 * These map user-friendly labels to the actual paths used by the backend.
 *
 * To add new sources (e.g., wrapper outputs), add a new entry here with:
 * - key: unique identifier (stored in criteria_path)
 * - label: user-friendly display name
 * - description: explanation of where the data comes from
 * - path: the actual template path (used internally)
 * - group: category for grouping in the dropdown
 */
interface CriteriaSourceOption {
  key: string
  label: string
  description: string
  path: string
  group: "test_case" | "test_case_attribute" | "wrapper" | "custom"
}

const TEST_CASE_ATTRIBUTE_KEY = "test_case_attribute"

const CRITERIA_SOURCE_OPTIONS: CriteriaSourceOption[] = [
  {
    key: "expected_behaviors",
    label: "Expected Behaviors",
    description: "Use the expected behaviors defined in each test case",
    path: "expected_behaviors",
    group: "test_case",
  },
  {
    key: TEST_CASE_ATTRIBUTE_KEY,
    label: "Custom Attribute",
    description:
      "Use a custom attribute from the test case's expected output JSON",
    path: "",
    group: "test_case_attribute",
  },
]

interface CompositeEvaluationConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export const CompositeEvaluationConfig: React.FC<
  CompositeEvaluationConfigProps
> = ({ metricRecordData, onChange }) => {
  const baseId = useId()
  const criteriaSourceLabelId = `${baseId}-criteria-source-label`
  const attributeFieldId = `${baseId}-attribute-field`
  const attributePathId = `${baseId}-attribute-path`
  const criteriaLabelId = `${baseId}-criteria-label`
  const automaticExpectedBehaviorEnabled = isAutomaticExpectedBehaviorEnabled()
  const criteriaSource =
    metricRecordData.metric_metadata?.criteria_source || "test_case"
  const criteriaPath = metricRecordData.metric_metadata?.criteria_path || ""
  const criteria = metricRecordData.metric_metadata?.criteria || []
  const basePromptTemplate =
    metricRecordData.metric_metadata?.base_prompt_template || ""

  // Determine which predefined source is selected
  const getSelectedSourceKey = (): string => {
    if (criteriaSource === "test_case_attribute") {
      return TEST_CASE_ATTRIBUTE_KEY
    }
    if (criteriaSource !== "test_case") {
      return ""
    }
    const matchingOption = CRITERIA_SOURCE_OPTIONS.find(
      (opt) => opt.group === "test_case" && opt.path === criteriaPath,
    )
    // Default to first option if no match
    return matchingOption?.key || CRITERIA_SOURCE_OPTIONS[0]?.key || ""
  }

  const selectedSourceKey = getSelectedSourceKey()
  const isCustomAttribute = selectedSourceKey === TEST_CASE_ATTRIBUTE_KEY

  // Set when the user switches to static criteria, so the first criterion
  // field takes focus once it mounts, but never on page load.
  const focusRevealedCriterionRef = React.useRef(false)
  const focusRevealedCriterion = React.useCallback(
    (node: HTMLInputElement | null) => {
      if (node && focusRevealedCriterionRef.current) {
        focusRevealedCriterionRef.current = false
        node.focus()
      }
    },
    [],
  )

  const handleCriteriaSourceChange = (
    value: "test_case" | "test_case_attribute" | "metric_metadata",
  ) => {
    const newMetadata = {
      ...metricRecordData.metric_metadata,
      criteria_source: value,
    }

    // Clear the other field when switching
    if (value === "test_case" || value === "test_case_attribute") {
      newMetadata.criteria = []
      if (value === "test_case") {
        const defaultOption = CRITERIA_SOURCE_OPTIONS.find(
          (opt) => opt.key === "expected_behaviors",
        )
        newMetadata.criteria_path = defaultOption?.path || ""
      } else {
        newMetadata.criteria_path = ""
      }
    } else {
      newMetadata.criteria_path = ""
      // Initialize with one empty criterion for better UX
      newMetadata.criteria = [""]
      focusRevealedCriterionRef.current = true
    }

    onChange({
      ...metricRecordData,
      metric_metadata: newMetadata,
    })
  }

  const handleSourceOptionChange = (value: string) => {
    const option = CRITERIA_SOURCE_OPTIONS.find((opt) => opt.key === value)
    if (!option) {
      return
    }

    if (option.key === TEST_CASE_ATTRIBUTE_KEY) {
      handleCriteriaSourceChange("test_case_attribute")
    } else {
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metricRecordData.metric_metadata,
          criteria_source: "test_case",
          criteria_path: option.path,
        },
      })
    }
  }

  const handleAttributePathChange = (value: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        criteria_path: value,
      },
    })
  }

  const handleAddCriterion = () => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        criteria: [...criteria, ""],
      },
    })
  }

  const handleRemoveCriterion = (index: number) => {
    const newCriteria = criteria.filter((_, i) => i !== index)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        criteria: newCriteria,
      },
    })
  }

  const handleCriterionChange = (index: number, value: string) => {
    const newCriteria = [...criteria]
    newCriteria[index] = value
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        criteria: newCriteria,
      },
    })
  }

  const handleBasePromptTemplateChange = (value: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        base_prompt_template: value,
      },
    })
  }

  const showTranscriptFilter = supportsTranscriptFilter(metricRecordData)
  const showTraceContext = supportsTraceContext(metricRecordData)

  // Group options by category
  const testCaseOptions = CRITERIA_SOURCE_OPTIONS.filter(
    (opt) => opt.group === "test_case" && !isAutomaticExpectedBehaviorEnabled(),
  )
  const testCaseAttributeOptions = CRITERIA_SOURCE_OPTIONS.filter(
    (opt) => opt.group === "test_case_attribute",
  )
  const wrapperOptions = CRITERIA_SOURCE_OPTIONS.filter(
    (opt) => opt.group === "wrapper",
  )

  const selectedStyles = "bg-surface-default text-coval-text shadow-sm"
  const unselectedStyles = "text-coval-text-muted hover:text-coval-text"

  if (isEnabledAutomaticExpectedBehaviorMetric(metricRecordData)) {
    return (
      <p className="text-sm text-coval-text-muted" role="status">
        Expected Behaviors are evaluated automatically as a percentage on
        simulation runs. Define the criteria and evaluation instructions in each
        test case. Existing custom metrics keep their saved configuration.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      {automaticExpectedBehaviorEnabled ? (
        <CompositeCriteriaFields
          criteriaSource={criteriaSource}
          criteriaPath={criteriaPath}
          criteria={criteria}
          onSourceChange={handleCriteriaSourceChange}
          onPathChange={handleAttributePathChange}
          onCriterionChange={handleCriterionChange}
          onAddCriterion={handleAddCriterion}
          onRemoveCriterion={handleRemoveCriterion}
        />
      ) : (
        <>
          <div className={FIELD_CONTAINER_STYLES}>
            <div className="flex items-center gap-1">
              <GroupLabel id={criteriaSourceLabelId} className={LABEL_STYLES}>
                Criteria Source
              </GroupLabel>
              <Tooltip
                content={
                  <div className="max-w-xs">
                    <p className="mb-2">
                      Choose where criteria come from: use dynamic criteria from
                      test cases, or define a fixed list that applies to all
                      runs.
                    </p>
                    <a
                      href={DOCS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-coval-info-text text-xs hover:underline"
                    >
                      Docs <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                }
              >
                <button type="button" className="inline-flex" aria-label="Info">
                  <Info className="w-3.5 h-3.5 text-coval-text-muted" />
                </button>
              </Tooltip>
            </div>
            <div
              role="group"
              aria-labelledby={criteriaSourceLabelId}
              className="flex h-control rounded-md bg-surface-1 p-1"
            >
              <button
                type="button"
                onClick={() =>
                  handleCriteriaSourceChange(
                    isAutomaticExpectedBehaviorEnabled()
                      ? "test_case_attribute"
                      : "test_case",
                  )
                }
                className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all ${
                  criteriaSource === "test_case" ||
                  criteriaSource === "test_case_attribute"
                    ? selectedStyles
                    : unselectedStyles
                }`}
              >
                From Test Case
              </button>
              <button
                type="button"
                onClick={() => handleCriteriaSourceChange("metric_metadata")}
                className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all ${
                  criteriaSource === "metric_metadata"
                    ? selectedStyles
                    : unselectedStyles
                }`}
              >
                Static Criteria
              </button>
            </div>
          </div>

          {(criteriaSource === "test_case" ||
            criteriaSource === "test_case_attribute") && (
            <>
              <div className={FIELD_CONTAINER_STYLES}>
                <div className="flex items-center gap-1">
                  <label
                    htmlFor={attributeFieldId}
                    id={`${attributeFieldId}-label`}
                    className={LABEL_STYLES}
                  >
                    Attribute Field
                  </label>
                  <Tooltip
                    content={
                      <div className="max-w-xs">
                        <p>
                          Select the test case field that contains the list of
                          criteria. Each criterion will be evaluated separately
                          against the transcript.
                        </p>
                      </div>
                    }
                  >
                    <button
                      type="button"
                      className="inline-flex"
                      aria-label="Info"
                    >
                      <Info className="w-3.5 h-3.5 text-coval-text-muted" />
                    </button>
                  </Tooltip>
                </div>
                <Select
                  value={selectedSourceKey}
                  onValueChange={handleSourceOptionChange}
                >
                  <SelectTrigger
                    id={attributeFieldId}
                    aria-labelledby={`${attributeFieldId}-label`}
                  >
                    <SelectValue placeholder="Select attribute field" />
                  </SelectTrigger>
                  <SelectContent>
                    {testCaseOptions.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Test Case Fields</SelectLabel>
                        {testCaseOptions.map((option) => (
                          <SelectItem
                            key={option.key}
                            value={option.key}
                            description={option.description}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {testCaseOptions.length > 0 &&
                      testCaseAttributeOptions.length > 0 && (
                        <SelectSeparator />
                      )}
                    {testCaseAttributeOptions.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Custom Attributes</SelectLabel>
                        {testCaseAttributeOptions.map((option) => (
                          <SelectItem
                            key={option.key}
                            value={option.key}
                            description={option.description}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {(testCaseOptions.length > 0 ||
                      testCaseAttributeOptions.length > 0) &&
                      wrapperOptions.length > 0 && <SelectSeparator />}
                    {wrapperOptions.length > 0 && (
                      <SelectGroup>
                        <SelectLabel>Wrapper Outputs</SelectLabel>
                        {wrapperOptions.map((option) => (
                          <SelectItem
                            key={option.key}
                            value={option.key}
                            description={option.description}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {isCustomAttribute && (
                <div className={FIELD_CONTAINER_STYLES}>
                  <div className="flex items-center gap-1">
                    <label htmlFor={attributePathId} className={LABEL_STYLES}>
                      Attribute Path
                      <RequiredMarker />
                    </label>
                    <Tooltip
                      content={
                        <div className="max-w-xs">
                          <p>
                            Dot-separated path to the attribute in the test
                            case&apos;s expected output JSON. The value at this
                            path must be a list of strings.
                          </p>
                        </div>
                      }
                    >
                      <button
                        type="button"
                        className="inline-flex"
                        aria-label="Info"
                      >
                        <Info className="w-3.5 h-3.5 text-coval-text-muted" />
                      </button>
                    </Tooltip>
                  </div>
                  <Input
                    type="text"
                    value={criteriaPath}
                    onChange={(e) => handleAttributePathChange(e.target.value)}
                    id={attributePathId}
                    placeholder="e.g., my_key or nested.key"
                  />
                  <p className="text-xs text-coval-warning-text mt-1">
                    This metric will fail if the attribute is not set on the
                    test case.
                  </p>
                </div>
              )}
            </>
          )}
          {/* Conditional: Static Criteria (when source is metric_metadata) */}
          {criteriaSource === "metric_metadata" && (
            <div className={FIELD_CONTAINER_STYLES}>
              <div className="flex items-center gap-1">
                <GroupLabel id={criteriaLabelId} className={LABEL_STYLES}>
                  Criteria
                  <RequiredMarker />
                </GroupLabel>
                <Tooltip
                  content={
                    <div className="max-w-xs">
                      <p>
                        Define the criteria to check in every run. Each
                        criterion is evaluated as MET, NOT_MET, or UNKNOWN based
                        on the transcript content.
                      </p>
                    </div>
                  }
                >
                  <button
                    type="button"
                    className="inline-flex"
                    aria-label="Info"
                  >
                    <Info className="w-3.5 h-3.5 text-coval-text-muted" />
                  </button>
                </Tooltip>
              </div>
              <div
                role="group"
                aria-labelledby={criteriaLabelId}
                className="space-y-2"
              >
                {criteria.map((criterion, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="text"
                      className="flex-1"
                      value={criterion}
                      onChange={(e) =>
                        handleCriterionChange(index, e.target.value)
                      }
                      placeholder={`Criterion ${index + 1}`}
                      aria-label={`Criterion ${index + 1}`}
                      ref={index === 0 ? focusRevealedCriterion : undefined}
                    />
                    <Button
                      icon
                      variant="ghost"
                      onClick={() => handleRemoveCriterion(index)}
                      tooltip={`Remove criterion ${index + 1}`}
                      className="h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4 text-coval-danger-text" />
                    </Button>
                  </div>
                ))}
                <Button variant="secondary" onClick={handleAddCriterion}>
                  <Plus className="w-4 h-4" />
                  Add Criterion
                </Button>
                {criteria.filter((c) => c.trim()).length === 0 && (
                  <p className="text-xs text-coval-warning-text">
                    At least one criterion is required
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Optional: Custom Evaluation Prompt */}
      <div className={FIELD_CONTAINER_STYLES}>
        <div className="flex items-center gap-1">
          <label className={LABEL_STYLES}>
            Custom Evaluation Prompt{" "}
            {automaticExpectedBehaviorEnabled ? (
              <em>optional</em>
            ) : (
              "(Optional)"
            )}
          </label>
          <Tooltip
            content={
              <div className="max-w-xs">
                <p className="mb-2">
                  Override the default evaluation prompt. Use{" "}
                  <code className="bg-surface-1 px-1 rounded">
                    {"{criterion}"}
                  </code>{" "}
                  as a placeholder that gets replaced with each criterion text.
                </p>
                <a
                  href={DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-coval-info-text text-xs hover:underline"
                >
                  Docs <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            }
          >
            <button type="button" className="inline-flex" aria-label="Info">
              <Info className="w-3.5 h-3.5 text-coval-text-muted" />
            </button>
          </Tooltip>
        </div>
        <Textarea
          value={basePromptTemplate}
          onChange={(e) => handleBasePromptTemplateChange(e.target.value)}
          placeholder="e.g., Check if the agent satisfied this requirement: {criterion}"
          rows={4}
          className={
            automaticExpectedBehaviorEnabled
              ? "resize-none overflow-y-auto"
              : undefined
          }
          aria-label="Custom evaluation prompt template"
        />
        <p className="text-xs text-coval-text-muted mt-1">
          Use <code>{"{criterion}"}</code> as a placeholder that will be
          replaced with each criterion text.
        </p>
      </div>

      {showTraceContext && (
        <TraceContextToggle
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
      )}

      {/* Additional Settings */}
      <div className="grid grid-cols-3 gap-6">
        <KnowledgeBaseDisableControl
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        <LLMModelSelect
          metricRecordData={metricRecordData}
          onChange={onChange}
          modelSurface="composite_evaluation"
        />
        {showTranscriptFilter && (
          <TranscriptFilterToggle
            metricRecordData={metricRecordData}
            onChange={onChange}
          />
        )}
      </div>

      {/* Transcript Filter Details */}
      {showTranscriptFilter && (
        <TranscriptFilterDetails
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
      )}
    </div>
  )
}
